/**
 * The graph window's spots (`shared/spot.ts`): where a node made at a page point goes, in the
 * view shown. Used by the window's own drops, right clicks and pastes, and answered to other
 * windows dragging something onto it (`GRAPH_SPOT`).
 */
import type { Board } from "../../state/board";
import type { Diagram } from "../../state/diagram";
import type { Scene } from "../../state/scene";
import { drawsBoxes, type View } from "../../state/view";
import { GRAPH_SPOT, type Spot, type SpotAsked } from "../../shared/spot";

/** How far up and left of the pointer a new box's corner goes, so the box sits under it. */
const BOX_CORNER = { x: 40, y: 16 };

/** What a spot is worked out from. */
export interface SpotDeps {
  view: View;
  scene: Scene;
  diagram: Diagram;
  board: Board;
}

/** The view's own point under the page point `x`, `y` (world units of that view). */
export function viewPointAt({ view, scene, diagram, board }: SpotDeps, stage: HTMLElement, x: number, y: number): { x: number; y: number; z?: number } {
  const mode = view.mode();
  const area = (mode === "board" ? stage.querySelector("[data-board]") : drawsBoxes(mode) ? stage.querySelector("#diagram-canvas") : null) ?? stage;
  const r = area.getBoundingClientRect();
  const [px, py] = [x - r.left, y - r.top];
  if (mode === "board") return board.toWorld(px, py);
  if (drawsBoxes(mode)) return diagram.toWorld(px, py);
  return scene.toWorld(px, py);
}

/** The spot of `stage` (the graph window) under the page point `x`, `y`. */
export function spotIn(deps: SpotDeps, stage: HTMLElement, x: number, y: number): Spot {
  const mode = deps.view.mode();
  const at = viewPointAt(deps, stage, x, y);
  if (mode === "board") return { hint: null, first: [], place: (kind) => deps.board.placeFor(kind, at) };
  if (drawsBoxes(mode)) {
    const corner: [number, number] = [Math.round(at.x - BOX_CORNER.x), Math.round(at.y - BOX_CORNER.y)];
    return { hint: null, first: deps.diagram.fixEdits(), place: () => ({ diagram: corner }) };
  }
  if (mode === "document") return { hint: null, first: [], place: () => ({}) };
  return { hint: { x: at.x, y: at.y, z: at.z ?? 0 }, first: [], place: () => ({}) };
}

/** Answers other windows asking for the spot under a point of `stage`; returns the function that stops. */
export function answerSpots(deps: SpotDeps, stage: HTMLElement): () => void {
  const asked = (e: Event) => {
    const { x, y, answer } = (e as CustomEvent<SpotAsked>).detail;
    answer(spotIn(deps, stage, x, y));
  };
  stage.addEventListener(GRAPH_SPOT, asked);
  return () => stage.removeEventListener(GRAPH_SPOT, asked);
}
